/**
 * Bombae ATC → Quantity Selector
 *
 * After a product is added to cart, every "Add to Cart" button for that variant
 * transforms into a [ − qty + ] pill.  The user can adjust quantity inline;
 * decrementing to 0 reverts to the original ATC button.
 *
 * Usage: add `data-bombae-atc-variant="<variant_id>"` to any wrapper that
 * contains the ATC button.  The script finds the <button> or <a> inside and
 * replaces it with the quantity selector.
 */

(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  const CART_URL = window.routes?.cart_url ? window.routes.cart_url + '.js' : '/cart.js';
  const CART_CHANGE_URL = window.routes?.cart_change_url ? window.routes.cart_change_url + '.js' : '/cart/change.js';

  let cartMap = {}; // variantId (string) → quantity (number)
  let cartKeyMap = {}; // variantId (string) → line item key (string)

  function rebuildMaps(items) {
    cartMap = {};
    cartKeyMap = {};
    items.forEach(function (item) {
      var vid = String(item.variant_id);
      cartMap[vid] = item.quantity;
      cartKeyMap[vid] = item.key;
    });
  }

  function updateCartIconCount(itemCount) {
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;
    var oldCount = cartIcon.querySelector('.cart-count-bubble');
    if (itemCount > 0) {
      if (oldCount) {
        var num = oldCount.querySelector('[aria-hidden]') || oldCount.querySelector('span');
        if (num) num.textContent = itemCount;
      } else {
        var bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true">' + itemCount + '</span><span class="visually-hidden">' + itemCount + ' items</span>';
        cartIcon.appendChild(bubble);
      }
    } else {
      if (oldCount) oldCount.remove();
    }
  }

  async function fetchCart() {
    try {
      const res = await fetch(CART_URL, { credentials: 'same-origin' });
      const cart = await res.json();
      rebuildMaps(cart.items);
      updateCartIconCount(cart.item_count);
    } catch (e) {
      // silent – we'll just show ATC buttons
    }
  }

  /* Apply Shopify sections-rendering response to DOM.
     Updates cart-icon-bubble count without a second fetch.
     Cart-drawer is handled by Gokwik — no DOM update needed. */
  function applyCartSections(sections) {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      /* Try standalone section wrapper first (standard Dawn setup) */
      var bubbleWrap = document.getElementById('shopify-section-cart-icon-bubble');
      if (bubbleWrap) {
        bubbleWrap.innerHTML = sections['cart-icon-bubble'];
      } else {
        /* Cart icon is inlined inside the header section — update only the
           count bubble child, leaving the SVG and link intact */
        var cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) {
          var parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var newCount = parsed.querySelector('.cart-count-bubble');
          var oldCount = cartIcon.querySelector('.cart-count-bubble');
          if (oldCount) oldCount.remove();
          if (newCount) cartIcon.appendChild(newCount.cloneNode(true));
        }
      }
    }
  }

  async function changeCartQty(variantId, quantity) {
    var lineItemKey = cartKeyMap[String(variantId)];
    if (!lineItemKey) {
      await fetchCart();
      lineItemKey = cartKeyMap[String(variantId)];
      if (!lineItemKey) return;
    }

    const res = await fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id:           lineItemKey,
        quantity:     quantity,
        sections:     ['cart-icon-bubble'],
        sections_url: window.location.pathname
      }),
    });
    const data = await res.json();

    /* Apply sections from the change response — one round-trip, no secondary fetch */
    applyCartSections(data.sections);

    /* Rebuild cartMap/cartKeyMap for accurate pill rendering */
    await fetchCart();
    renderAll();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bombae-qty-atc' });
    }
  }

  /* ── CSS (injected once) ─────────────────────────────────────────────── */

  const STYLE_ID = 'bombae-qty-atc-css';
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      /* ── Quantity pill ── */
      .bombae-qty-pill {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 9999px;
        overflow: hidden;
        white-space: nowrap;
        box-sizing: border-box;
      }
      .bombae-qty-pill button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: inherit;
        cursor: pointer;
        padding: 0;
        margin: 0;
        line-height: 1;
        font-size: inherit;
        font-weight: 700;
        flex-shrink: 0;
        -webkit-appearance: none;
        appearance: none;
      }
      .bombae-qty-pill button:active { opacity: .6; }
      .bombae-qty-pill__value {
        text-align: center;
        font-weight: 700;
        line-height: 1;
        pointer-events: none;
        min-width: 1.4em;
      }

      /* ─── Product-page / buy-buttons variant ─── */
      .product-form__buttons .bombae-qty-pill {
        width: 100%;
        height: 100%;
        background: var(--bombae-atc-bg, #b987ff);
        color: var(--bombae-atc-color, #000);
        font-size: 16px;
        padding: 10px 16px;
      }
      .product-form__buttons .bombae-qty-pill button {
        width: 36px; height: 36px;
        font-size: 22px;
      }

      /* ─── Sticky bar desktop variant ─── */
      .bombae-sticky-atc__form .bombae-qty-pill {
        width: 100%;
        height: 40px;
        background: #b987ff;
        color: #000;
        font-size: 14px;
        padding: 0 12px;
      }
      .bombae-sticky-atc__form .bombae-qty-pill button {
        width: 30px; height: 30px;
        font-size: 18px;
      }

      /* ─── Sticky bar mobile variant ─── */
      .bombae-sticky-atc__mobile-right .bombae-qty-pill {
        width: 100%;
        height: 42px;
        background: #b987ff;
        color: #000;
        font-size: 14px;
        padding: 0 12px;
      }
      .bombae-sticky-atc__mobile-right .bombae-qty-pill button {
        width: 30px; height: 30px;
        font-size: 18px;
      }

      /* ─── Bombae card v1 ─── */
      .bm-card__bottom .bombae-qty-pill {
        background: #6a25b3;
        color: #fff;
        border-radius: 50px;
        padding: 7px 12px;
        font-size: 11px;
      }
      .bm-card__bottom .bombae-qty-pill button {
        width: 20px; height: 20px;
        font-size: 14px;
        color: #fff;
      }

      /* ─── Bombae card v1 (bm-card) ─── */
      .bm-card__body .bombae-qty-pill {
        background: #b987ff;
        color: #000;
        border-radius: 30px;
        padding: 8px 14px;
        font-size: 12.5px;
        font-weight: 700;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        width: 100%;
      }
      .bm-card__body .bombae-qty-pill button {
        width: 22px; height: 22px;
        font-size: 16px;
        color: #000;
      }

      /* ─── Bombae card v2 ─── */
      .bmc2__bottom .bombae-qty-pill {
        background: #6a25b3 !important;
        color: #fff !important;
        border-radius: 10px !important;
        padding: 7px 12px !important;
        font-size: 11px !important;
      }
      .bmc2__bottom .bombae-qty-pill button {
        width: 20px; height: 20px;
        font-size: 14px;
        color: #fff !important;
      }

      /* ─── Hairstyling products section ─── */
      .bombae-hs-products__info .bombae-qty-pill {
        background: #6a25b3;
        color: #fff;
        border-radius: 10px;
        padding: 0 12px;
        width: 100%;
        height: 38px;
        font-size: 1.1rem;
        font-weight: 700;
      }
      .bombae-hs-products__info .bombae-qty-pill button {
        width: 28px; height: 28px;
        font-size: 18px;
        color: #fff;
      }

      /* ─── Standard card quick-add ─── */
      .quick-add .bombae-qty-pill {
        width: 100%;
        background: rgba(var(--color-button),var(--alpha-button-background));
        color: rgb(var(--color-button-text));
        font-size: 14px;
        padding: 8px 14px;
        border-radius: 9999px;
      }
      .quick-add .bombae-qty-pill button {
        width: 28px; height: 28px;
        font-size: 18px;
        color: inherit;
      }

      /* ─── Hide original ATC when qty pill is active ─── */
      [data-bombae-atc-variant].bombae-qty-active > *:not(.bombae-qty-pill) {
        display: none !important;
      }
      [data-bombae-atc-variant]:not(.bombae-qty-active) > .bombae-qty-pill {
        display: none !important;
      }

      /* ─── ATC button loading: keep text visible, just dim ─── */
      [data-bombae-atc-variant] .button.loading {
        color: inherit;
        opacity: 0.55;
      }
      [data-bombae-atc-variant] .loading__spinner {
        display: none !important;
      }

      /* ─── Qty pill loading state ─── */
      .bombae-qty-pill--loading {
        opacity: .55;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }

  /* ── Build quantity pill HTML ────────────────────────────────────────── */

  function createQtyPill(variantId, qty) {
    const pill = document.createElement('div');
    pill.className = 'bombae-qty-pill';
    pill.dataset.bombaeQtyPill = variantId;
    pill.innerHTML =
      '<button type="button" data-action="minus" aria-label="Decrease quantity">&minus;</button>' +
      '<span class="bombae-qty-pill__value">' + qty + '</span>' +
      '<button type="button" data-action="plus" aria-label="Increase quantity">+</button>';

    pill.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var action = btn.dataset.action;
      var currentQty = cartMap[String(variantId)] || 0;
      var newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;
      if (newQty < 0) newQty = 0;

      pill.classList.add('bombae-qty-pill--loading');

      changeCartQty(variantId, newQty).then(function () {
        renderAll();
        if (action === 'plus') fireAddToast();
      }).catch(function () {
        pill.classList.remove('bombae-qty-pill--loading');
      });
    });

    return pill;
  }

  /* ── Render / update all ATC wrappers ────────────────────────────────── */

  function renderAll() {
    document.querySelectorAll('[data-bombae-atc-variant]').forEach(function (wrapper) {
      var vid = String(wrapper.dataset.bombaeAtcVariant);
      var qty = cartMap[vid] || 0;
      var existingPill = wrapper.querySelector('.bombae-qty-pill');

      if (qty > 0) {
        // Ensure pill exists
        if (!existingPill) {
          wrapper.appendChild(createQtyPill(vid, qty));
        } else {
          existingPill.querySelector('.bombae-qty-pill__value').textContent = qty;
          existingPill.classList.remove('bombae-qty-pill--loading');
        }
        wrapper.classList.add('bombae-qty-active');
        // Reset any loading state on the original ATC button now that pill is active
        var atcBtn = wrapper.querySelector('[type="submit"]');
        if (atcBtn) {
          atcBtn.classList.remove('loading');
          atcBtn.removeAttribute('aria-disabled');
          var spinner = wrapper.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      } else {
        // Remove pill, show original
        if (existingPill) {
          existingPill.remove();
        }
        wrapper.classList.remove('bombae-qty-active');
      }
    });
  }

  /* ── Watch for variant changes on product pages ───────────────────── */

  function watchVariantInputs() {
    // When variant ID input changes (user selects a different variant),
    // update the wrapper's data attribute and re-render
    document.querySelectorAll('input[name="id"].product-variant-id').forEach(function (input) {
      if (input._bombaeWatched) return;
      input._bombaeWatched = true;
      input.addEventListener('change', function () {
        var wrapper = input.closest('[data-bombae-atc-variant]');
        if (wrapper && input.value) {
          // Remove existing pill for old variant
          var oldPill = wrapper.querySelector('.bombae-qty-pill');
          if (oldPill) oldPill.remove();
          wrapper.classList.remove('bombae-qty-active');
          // Update attribute to new variant
          wrapper.dataset.bombaeAtcVariant = input.value;
          renderAll();
        }
      });
    });
  }

  /* ── Optimistic update from /cart/add.js response ───────────────────── */

  /* /cart/add.js returns the line item that was added (or {items:[...]}
     when multiple variants are added at once). We use it to seed cartMap
     so the qty pill swaps in immediately — no extra round-trip to /cart.js. */
  function applyOptimisticAdd(cartData) {
    if (!cartData) return false;
    var items = (cartData.items && cartData.items.length) ? cartData.items : [cartData];
    var changed = false;
    items.forEach(function (item) {
      if (!item || !item.variant_id) return;
      var vid = String(item.variant_id);
      // line.quantity from /cart/add.js is the line's total quantity post-add
      cartMap[vid] = item.quantity;
      if (item.key) cartKeyMap[vid] = item.key;
      changed = true;
    });
    return changed;
  }

  /* ── Coalesced cart refresh (microtask, no human-perceptible delay) ──── */

  var _cartRefreshScheduled = false;
  function scheduleCartRefresh() {
    if (_cartRefreshScheduled) return;
    _cartRefreshScheduled = true;
    Promise.resolve().then(function () {
      _cartRefreshScheduled = false;
      fetchCart().then(function () { renderAll(); });
    });
  }

  /* GoKwik's side-cart.js intercepts ATC clicks at the document level,
     calls preventDefault(), and does its own /cart/add via XHR — bypassing
     product-form.js entirely. So showBombaeToast is never called by the
     theme's add-to-cart code path. The interceptors below catch every
     successful /cart/add (regardless of who fired it) and fire the toast
     so the user always sees feedback. */
  var _lastToastAt = 0;
  function fireAddToast() {
    /* Coalesce multi-call bursts within 250ms (e.g. theme + GoKwik both fire) */
    var now = Date.now();
    if (now - _lastToastAt < 250) return;
    _lastToastAt = now;
    if (typeof window.showBombaeToast === 'function') {
      window.showBombaeToast('Product added to cart');
    }
  }

  /* ── XHR interceptor (catches GokWik and other XHR-based cart calls) ── */

  function installXHRInterceptor() {
    if (window._bombaeXHRIntercepted) return;
    window._bombaeXHRIntercepted = true;
    var _origOpen = XMLHttpRequest.prototype.open;
    var _origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._bombaeUrl = url;
      return _origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._bombaeUrl && /\/cart\/(add|update|change)/.test(this._bombaeUrl)) {
        var isAdd = /\/cart\/add/.test(this._bombaeUrl);
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            scheduleCartRefresh();
            if (isAdd) fireAddToast();
          }
        });
      }
      return _origSend.apply(this, arguments);
    };
  }

  /* ── Fetch interceptor (catches fetch-based cart calls) ─────────────── */

  function installFetchInterceptor() {
    if (window._bombaeCartFetchIntercepted) return;
    window._bombaeCartFetchIntercepted = true;

    // .bind(window) prevents "Illegal invocation" when fetch is called
    // from strict-mode class methods where `this` would be undefined
    var _origFetch = window.fetch.bind(window);
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input
              : (input && input.url) ? input.url : '';
      var result = _origFetch.apply(window, arguments);
      if (/\/cart\/(add|update|change)/.test(url)) {
        var isAdd = /\/cart\/add/.test(url);
        result.then(function (response) {
          if (response && response.ok) {
            scheduleCartRefresh();
            if (isAdd) fireAddToast();
          }
        }).catch(function () {});
      }
      return result;
    };
  }

  // Global hook so product-form.js can trigger a refresh directly
  window.bombaeQtyRefresh = scheduleCartRefresh;

  /* ── Bootstrap ───────────────────────────────────────────────────────── */

  function init() {
    installFetchInterceptor();
    installXHRInterceptor();

    fetchCart().then(function () {
      renderAll();
      watchVariantInputs();
    });

    // Also listen for theme PubSub cart updates as a secondary trigger
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source === 'bombae-qty-atc') return;
        // Optimistic instant render using the /cart/add.js payload that
        // product-form.js attaches as `cartData`. Pill swaps before the
        // background fetch even fires.
        if (evt && evt.cartData && applyOptimisticAdd(evt.cartData)) {
          renderAll();
          if (evt.cartData.sections) applyCartSections(evt.cartData.sections);
        }
        return fetchCart().then(function () {
          renderAll();
        });
      });
    }
  }

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
